import React from "react";

function gradeColor(g) {
  if (g == null) return "bg-slate-200 text-slate-500";
  if (g >= 60) return "bg-[#E63946] text-white";
  if (g >= 50) return "bg-[#0A192F] text-white";
  if (g >= 40) return "bg-amber-200 text-amber-900";
  return "bg-slate-300 text-slate-700";
}

const STATUS_STYLE = {
  submitted: "bg-emerald-100 text-emerald-800",
  draft: "bg-amber-100 text-amber-800",
};

/**
 * Compact card displaying a single 20/80 scouting evaluation.
 * Used in: admin player detail, public player profile (only submitted ones).
 */
export default function EvaluationCard({ evaluation, hideEvaluator = false, onEdit = null, onDelete = null }) {
  const e = evaluation;
  if (!e) return null;
  return (
    <div data-testid={`eval-card-${e.id}`} className="border border-slate-200 rounded-sm p-5 bg-white">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-overline">{e.role_archetype?.replace("_", " ")} · {e.position_evaluated || "—"}</div>
          <div className="font-display text-lg uppercase tracking-tight mt-1">
            {e.camp_name || "Independent Evaluation"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {e.event_date ? new Date(e.event_date).toLocaleDateString("en-CA", { dateStyle: "medium" }) : ""}
            {!hideEvaluator && e.evaluator_name ? ` · ${e.evaluator_name}` : ""}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {e.overall_future_value != null && (
            <div className="text-right">
              <div className="label-overline">OFV</div>
              <div className={`font-data text-2xl px-3 py-1 mt-1 rounded-sm ${gradeColor(e.overall_future_value)}`}>
                {e.overall_future_value}
              </div>
            </div>
          )}
          <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${STATUS_STYLE[e.status]}`}>
            {e.status}
          </span>
          {(onEdit || onDelete) && (
            <div className="flex items-center gap-2 mt-1">
              {onEdit && (
                <button data-testid={`eval-edit-${e.id}`} onClick={() => onEdit(e)}
                  className="text-[10px] font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946]">Edit</button>
              )}
              {onDelete && (
                <button data-testid={`eval-delete-${e.id}`} onClick={() => onDelete(e)}
                  className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-[#E63946]">Delete</button>
              )}
            </div>
          )}
        </div>
      </div>

      {Object.keys(e.tools || {}).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(e.tools).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1 text-xs">
              <span className="label-overline text-[10px]">{k.replace(/_/g, " ")}</span>
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${gradeColor(v)}`}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {e.recommended_level && (
        <div className="mt-3 text-xs">
          <span className="label-overline">Projection:</span>{" "}
          <span className="font-semibold text-[#0A192F]">{e.recommended_level}</span>
        </div>
      )}

      {(e.strengths || e.development_areas) && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
          {e.strengths && <div><div className="label-overline">Strengths</div><p className="mt-1 text-slate-700 leading-relaxed">{e.strengths}</p></div>}
          {e.development_areas && <div><div className="label-overline">Development</div><p className="mt-1 text-slate-700 leading-relaxed">{e.development_areas}</p></div>}
        </div>
      )}

      {e.written_report && (
        <details className="mt-3 text-sm">
          <summary className="label-overline cursor-pointer">Full Report</summary>
          <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-line">{e.written_report}</p>
        </details>
      )}

      {(e.video_urls || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {e.video_urls.map((v, i) => (
            <a key={i} href={v} target="_blank" rel="noreferrer" className="text-xs underline text-[#E63946]">Video {i + 1} ↗</a>
          ))}
        </div>
      )}
    </div>
  );
}
